// ANCHOR React
import React, { useState } from 'react';

// ANCHOR Linkify
import Linkify from 'react-linkify';

// ANCHOR Base
import { styled } from 'baseui';
import { Block } from 'baseui/block';
import { LabelSmall } from 'baseui/typography';
import { ChevronDown, ChevronRight, ChevronUp } from 'baseui/icon';
import { Button } from 'baseui/button';
import { StyledLink } from 'baseui/link';

// ANCHOR Models
import { useProviderCourseError } from '@lyon/scoped-models/ProviderCourseData';
import { ProviderCheckManager } from '@lyon/scoped-models/ProviderCheckManager';
import { MobileView } from '@lyon/scoped-models/MobileView';

// ANCHOR Hooks
import { useConstantCallback } from '@lyon/utils/hooks/useConstantCallback';
import { useToggle } from '@lyon/utils/hooks/useToggle';

// ANCHOR Results
import { ICourseResult } from '@lyon/utils/api/results/course';

// ANCHOR Components
import { LyonCourseCardSkeleton } from '@lyon/components/course-card-revision/LyonCourseCardSkeleton';
import { LyonCourseCardRevision } from '@lyon/components/course-card-revision/LyonCourseCard';
import { LyonEmptyState } from '@lyon/components/empty/LyonEmptyState';

// ANCHOR Constants
import { EMPTY } from './constants';

// ANCHOR Styles
import {
  FEATURED_CONTAINER, NORMAL, NORMAL_FADED, PURPLE, ROOT,
} from './styles';

interface IProps {
  data?: ICourseResult[];
}

const ClickableDiv = styled('div', {
  marginTop: '8px',
  background: 'none',
  cursor: 'pointer',
});

const MORE = <ChevronDown size={20} />;
const LESS = <ChevronUp size={20} />;
const NEXT = <ChevronRight size={16} />;

const COMPONENT_DECORATOR = (href: string, text: string, key: number) => (
  <StyledLink href={href} target="_blank" rel="noreferrer noopener" key={key}>
    {text}
  </StyledLink>
);

export function LyonProviderFeaturedCourses({ data }: IProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isProvider = ProviderCheckManager.useSelector((state) => state.data);
  const isMobile = MobileView.useSelector((state) => state.isMobile);

  const error = useProviderCourseError();

  const [descriptionHeight, setDescriptionHeight] = useState(0);
  const { toggle, state: shouldTruncate } = useToggle(false);
  const descriptionRef = useConstantCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setDescriptionHeight(node.getBoundingClientRect().height);
    }
  });
  const isDescriptionLong = descriptionHeight >= 240;

  const onIndexChange = useConstantCallback((index: number, maxIndex: number) => {
    setCurrentIndex(index === maxIndex ? 0 : index + 1);
  });

  if (data) {
    if (data.length) {
      return (
        <>
          {
            data.map((course, index, courses) => {
              const maxIndex = courses.length - 1;

              if (currentIndex !== index) {
                return null;
              }

              return (
                <Block overrides={ROOT} key={index}>
                  <Block overrides={FEATURED_CONTAINER}>
                    <LyonCourseCardRevision
                      course={course}
                      fromProvider
                      isProviderManager={!!isProvider}
                    />
                    <Block>
                      <Linkify componentDecorator={COMPONENT_DECORATOR}>
                        <LabelSmall
                          overrides={(isDescriptionLong && !shouldTruncate)
                            ? NORMAL_FADED
                            : NORMAL}
                          ref={descriptionRef}
                        >
                          {course.description}
                        </LabelSmall>
                      </Linkify>
                      {isDescriptionLong && (
                        <ClickableDiv onClick={toggle}>
                          <LabelSmall overrides={PURPLE}>
                            {`Show ${shouldTruncate ? 'less' : 'more'}`}
                            {' '}
                            {shouldTruncate ? LESS : MORE}
                          </LabelSmall>
                        </ClickableDiv>
                      )}
                    </Block>
                  </Block>
                  {
                    maxIndex > 0 && (
                      <Block>
                        <Button
                          size="default"
                          kind="secondary"
                          type="button"
                          onClick={() => onIndexChange(index, maxIndex)}
                        >
                          {isMobile ? 'Next Featured Course' : NEXT}
                        </Button>
                      </Block>
                    )
                  }
                </Block>
              );
            })
          }
        </>
      );
    }

    return <LyonEmptyState label={EMPTY} hasImage />;
  }

  if (error) {
    return <LyonEmptyState label={EMPTY} hasImage />;
  }

  return <LyonCourseCardSkeleton />;
}
